/**
 * 地区メニューの単一の真実の源 (Phase 2 IA 統一)。
 *
 * サイドバー (AdminLayout の地区サブメニュー) と 地区概要ページ (DistrictDetailPage のカード) が
 * **同じ定義・同じ並び順・同じグループ・同じ日本語ラベル** を参照するための定数。これまで両者が
 * 別々にラベル/順序を持ち、初めての管理者が「同じ地区なのに入口で項目順が違う」と迷っていた問題を解消する。
 *
 * グループは業務フロー順 (この地区について → 設定 → 運用 → データ) の初心者向け日本語。
 * ラベル/説明は開発者ジャーゴンを排した平易な日本語 (Phase 1 で確定したもの)。
 */

export type DistrictMenuGroup = 'この地区について' | '設定' | '運用' | 'データ';

/** グループの表示順 (サイドバー・概要ページ共通)。 */
export const DISTRICT_MENU_GROUP_ORDER: DistrictMenuGroup[] = [
  'この地区について',
  '設定',
  '運用',
  'データ',
];

export interface DistrictMenuItem {
  /** breadcrumb / 内部識別用キー。 */
  key: string;
  /** `/districts/:id` からの相対サフィックス (概要は空文字)。 */
  routeSuffix: string;
  label: string;
  description?: string;
  group: DistrictMenuGroup;
}

/**
 * 地区配下の全機能 (業務フロー順)。概要を先頭に置き、以降を 設定 / 運用 / データ にグループ化。
 * 並び順 = 配列順。サイドバーと概要ページはこの順序をそのまま使う。
 */
export const DISTRICT_MENU: DistrictMenuItem[] = [
  // この地区について
  { key: 'overview', routeSuffix: '', label: '概要', group: 'この地区について' },
  {
    key: 'gis',
    routeSuffix: '/gis',
    label: 'GISデータ',
    description: '地図に重ねるGIS地図データ（GeoPackage / KML / GeoJSON）の取込・色分け',
    group: 'この地区について',
  },
  // 設定
  {
    key: 'data-settings',
    routeSuffix: '/data-settings',
    label: '入力項目',
    description: '参加者登録と投稿の入力項目を編集',
    group: '設定',
  },
  {
    key: 'display',
    routeSuffix: '/display',
    label: '地図・表示の設定',
    description: '地図の色分け・スタイル・投稿範囲・オフライン地図・分析画面の表示',
    group: '設定',
  },
  {
    key: 'ai',
    routeSuffix: '/ai',
    label: 'AI 設定',
    description: 'AI解析・対話・検証・再解析の設定（マスター管理者のみ）',
    group: '設定',
  },
  {
    key: 'misc',
    routeSuffix: '/misc',
    label: 'その他設定',
    description:
      'アプリ導入案内・外部アンケート・地図ダウンロード方法・写真のプライバシー（顔・ナンバーの自動ぼかし）などの細かい設定',
    group: '設定',
  },
  // 運用
  {
    key: 'moderation',
    routeSuffix: '/moderation',
    label: '投稿モデレーション',
    description: '投稿の公開状態の切替・削除',
    group: '運用',
  },
  {
    key: 'street-survey',
    routeSuffix: '/street-survey',
    label: 'ストリートビュー机上調査',
    description: '座標を指定して Street View を AI 解析（docs/25、master・地区管理者向け）',
    group: '運用',
  },
  {
    key: 'insights-moderation',
    routeSuffix: '/insights-moderation',
    label: '気づき・処方箋のモデレーション',
    description: '気づきカード・処方箋カードの公開・非公開・削除',
    group: '運用',
  },
  {
    key: 'announcements',
    routeSuffix: '/announcements',
    label: 'お知らせ',
    description: '画面上部のお知らせ・起動時のお知らせの作成と再表示',
    group: '運用',
  },
  {
    key: 'sessions',
    routeSuffix: '/sessions',
    label: 'セッション',
    description: 'まち歩きセッションの登録・非表示',
    group: '運用',
  },
  {
    // 2026-09-07 追加。マスター管理者は常に表示、地区管理者は master が地区ごとに許可した場合のみ
    // (districtNavGroups の participantAdminVisible)。
    key: 'participants',
    routeSuffix: '/participants',
    label: 'ユーザー管理',
    description: '参加者の登録データ（ニックネーム・属性・参加セッション）の確認と修正',
    group: '運用',
  },
  {
    key: 'surveys',
    routeSuffix: '/surveys',
    label: '運用中のアンケート',
    description: 'この地区のアンケートの作成・配信',
    group: '運用',
  },
  {
    key: 'verification',
    routeSuffix: '/verification/templates',
    label: '運用中の検証',
    description: 'AI解析結果を参加者に検証してもらうテンプレートの管理',
    group: '運用',
  },
  {
    // 2026-09-04: 旧 `/verification/distribute` (ID 直貼りの旧画面) から、配分セット一覧
    // (予約 + 配分セット) に付け替え。旧画面はルートだけ残し導線からは外した。
    key: 'verification-assignments',
    routeSuffix: '/verification/assignmentSets',
    label: '検証の配分',
    description:
      '検証をだれに配るかを決めて実行します（セッション終了後にまとめて配る予約もここから）',
    group: '運用',
  },
  {
    key: 'verification-results',
    routeSuffix: '/verification-results',
    label: '検証結果',
    description: '割当セットごとの検証結果の集計',
    group: '運用',
  },
  // データ
  {
    key: 'export',
    routeSuffix: '/export',
    label: 'エクスポート',
    description: 'CSV / GeoJSON で書き出し（個人情報を除外する切替あり）',
    group: 'データ',
  },
  {
    key: 'import',
    routeSuffix: '/import',
    label: 'インポート',
    description: 'CSV・Googleスプレッドシート・Driveの写真から取込（取込前にプレビュー）',
    group: 'データ',
  },
  {
    key: 'photo-import',
    routeSuffix: '/photo-import',
    label: '写真まとめて一括投稿',
    description: '複数の写真を取り込み、EXIF位置で地図に反映して参加者名義で一括投稿 (issue #23)',
    group: 'データ',
  },
  {
    key: 'cards',
    routeSuffix: '/export/cards',
    label: 'カード印刷',
    description: '投稿カードをA4・10面付けのPDFで印刷',
    group: 'データ',
  },
];

export interface DistrictNavItem {
  key: string;
  to: string;
  label: string;
  description?: string;
}

export interface DistrictNavGroup {
  group: DistrictMenuGroup;
  items: DistrictNavItem[];
}

/**
 * 指定地区の絶対 route 付きメニューを、グループ順にまとめて返す (サイドバー・概要ページ共通)。
 * `includeOverview=false` (既定) のとき概要 (親リンク / ページ自身) を除外する。
 * `streetViewAnalysisEnabled` に明示的に `false` を渡すと、この地区でストリートビュー机上調査が
 * 未有効化 (docs/25 §10-1、master 未設定) とみなして「ストリートビュー机上調査」項目を除外する。
 * `undefined` (地区データ未ロード / 不明) のときは誤非表示を避けて項目を残す。
 * `participantAdminVisible` に `false` を渡すと「ユーザー管理」項目を除外する
 * (地区管理者で、master が `participantAdmin.districtAdminCanManage` を ON にしていない地区)。
 * 空グループは返さない。
 */
export function districtNavGroups(
  districtId: string,
  opts?: {
    includeOverview?: boolean;
    streetViewAnalysisEnabled?: boolean;
    participantAdminVisible?: boolean;
  },
): DistrictNavGroup[] {
  const base = `/districts/${districtId}`;
  const includeOverview = opts?.includeOverview ?? false;
  const hideStreetSurvey = opts?.streetViewAnalysisEnabled === false;
  const hideParticipants = opts?.participantAdminVisible === false;
  return DISTRICT_MENU_GROUP_ORDER.map((group) => ({
    group,
    items: DISTRICT_MENU.filter(
      (i) =>
        i.group === group &&
        (includeOverview || i.key !== 'overview') &&
        !(hideStreetSurvey && i.key === 'street-survey') &&
        !(hideParticipants && i.key === 'participants'),
    ).map((i) => ({
      key: i.key,
      to: `${base}${i.routeSuffix}`,
      label: i.label,
      description: i.description,
    })),
  })).filter((g) => g.items.length > 0);
}
